import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ThumbnailsPane, ThumbImg } from '@embedpdf/plugin-thumbnail/react'
import { useScroll } from '@embedpdf/plugin-scroll/react'
import {
  StampImg,
  useStampLibraries,
  useStampsByLibrary,
  useActiveStamp,
  useStampCapability,
} from '@embedpdf/plugin-stamp/react'
import { ignore } from '@embedpdf/models'
import { TabButton } from '../ui/tab-button'
import { Icon } from '../ui/icon'
import { useResponsive } from '../../hooks/useResponsive'

export type SidebarTab = 'Thumbnails' | 'Outline' | 'Stamps'
export const openStampSidebarRef: { current: (() => void) | null } = { current: null }

interface SidebarProps {
  documentId: string
  isOpen: boolean
  onClose: () => void
}

const STAMP_THUMB_WIDTH = 120

export const Sidebar = ({ documentId, isOpen, onClose }: SidebarProps) => {
  const { t } = useTranslation()
  const { state, provides: scrollApi } = useScroll(documentId)
  const [activeTab, setActiveTab] = useState<SidebarTab>('Thumbnails')
  const isCompact = useResponsive()

  useEffect(() => {
    openStampSidebarRef.current = () => setActiveTab('Stamps')
    return () => { openStampSidebarRef.current = null }
  }, [])

  if (!isOpen) return null

  if (isCompact) {
    return (
      <div className="fixed inset-0 z-50 flex items-end" onClick={onClose}>
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative w-full max-h-[50vh] bg-bg-surface rounded-t-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom duration-300 shadow-2xl" onClick={(e) => e.stopPropagation()}>
          <div className="flex-shrink-0 flex justify-center pt-3 pb-1">
            <div className="w-10 h-1 rounded-full bg-border-default" />
          </div>
          <div className="flex p-2 gap-1 bg-bg-app border-b border-border-default px-4">
            <TabButton
              active={activeTab === 'Thumbnails'}
              onClick={() => setActiveTab('Thumbnails')}
            >
              <Icon icon="file" size={14} className="mr-1.5" />
              <span>{t('sidebar.thumbnails') || 'Pages'}</span>
            </TabButton>
            <TabButton
              active={activeTab === 'Outline'}
              onClick={() => setActiveTab('Outline')}
            >
              <Icon icon="list" size={14} className="mr-1.5" />
              <span>{t('sidebar.outline') || 'Outline'}</span>
            </TabButton>
            <TabButton
              active={activeTab === 'Stamps'}
              onClick={() => setActiveTab('Stamps')}
            >
              <Icon icon="stamp" size={14} className="mr-1.5" />
              <span>{t('sidebar.stamps') || 'Stamps'}</span>
            </TabButton>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-6">
            <TabContent documentId={documentId} activeTab={activeTab} scrollApi={scrollApi} state={state} t={t} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-[280px] bg-bg-surface border-r border-border-default flex flex-col overflow-hidden animate-in slide-in-from-left duration-300 relative z-10">
      <div className="flex p-2 gap-1 bg-bg-app border-b border-border-default">
        <TabButton
          onClick={() => setActiveTab('Thumbnails')}
          active={activeTab === 'Thumbnails'}
          className="flex-1 gap-2"
        >
          <Icon icon="squares" size={16} />
          <span className={`text-xs font-bold ${activeTab === 'Thumbnails' ? 'text-accent' : 'text-fg-secondary'}`}>{t('sidebar.thumbnails')}</span>
        </TabButton>
        <TabButton
          onClick={() => setActiveTab('Outline')}
          active={activeTab === 'Outline'}
          className="flex-1 gap-2"
        >
          <Icon icon="listTree" size={16} />
          <span className={`text-xs font-bold ${activeTab === 'Outline' ? 'text-accent' : 'text-fg-secondary'}`}>{t('sidebar.outline')}</span>
        </TabButton>
        <TabButton
          onClick={() => setActiveTab('Stamps')}
          active={activeTab === 'Stamps'}
          className="flex-1 gap-2"
        >
          <Icon icon="rubberStamp" size={16} />
          <span className={`text-xs font-bold ${activeTab === 'Stamps' ? 'text-accent' : 'text-fg-secondary'}`}>{t('sidebar.stamps')}</span>
        </TabButton>
      </div>

      <div className="flex-1 relative overflow-hidden bg-bg-app">
        <TabContent documentId={documentId} activeTab={activeTab} scrollApi={scrollApi} state={state} t={t} />
      </div>
    </div>
  )
}

interface TabContentProps {
  documentId: string
  activeTab: SidebarTab
  scrollApi: any
  state: any
  t: (key: string) => string
}

function TabContent({ documentId, activeTab, scrollApi, state, t }: TabContentProps) {
  if (activeTab === 'Thumbnails') {
    return (
      <ThumbnailsPane documentId={documentId}>
        {(meta) => {
          const isActive = state.currentPage === meta.pageIndex + 1
          return (
            <div
              key={meta.pageIndex}
              className="absolute flex w-full cursor-pointer flex-col items-center px-6"
              style={{ height: meta.wrapperHeight, top: meta.top }}
              onClick={() => scrollApi?.scrollToPage?.({ pageNumber: meta.pageIndex + 1 })}
            >
              <div className={`overflow-hidden rounded-md border-2 transition-all shadow-lg ${isActive ? 'border-accent ring-4 ring-interactive-focus-ring scale-105' : 'border-border-default hover:border-border-strong'}`} style={{ width: meta.width, height: meta.height }}>
                <ThumbImg documentId={documentId} meta={meta} className="h-full w-full object-contain bg-white" />
              </div>
              <div className="mt-2 flex items-center justify-center" style={{ height: meta.labelHeight }}>
                <span className={`text-[10px] font-mono font-bold tracking-widest ${isActive ? 'text-accent' : 'text-fg-muted'}`}>{meta.pageIndex + 1}</span>
              </div>
            </div>
          )
        }}
      </ThumbnailsPane>
    )
  }

  if (activeTab === 'Outline') {
    return (
      <div className="p-8 flex flex-col items-center justify-center text-center gap-4 h-full">
        <div className="w-12 h-12 rounded-full bg-bg-surface flex items-center justify-center text-fg-muted">
          <Icon icon="listTree" size={24} />
        </div>
        <div>
          <h4 className="text-fg-primary text-sm font-bold mb-1">{t('sidebar.noOutline')}</h4>
          <p className="text-fg-muted text-xs">{t('sidebar.noOutlineHint')}</p>
        </div>
      </div>
    )
  }

  if (activeTab === 'Stamps') {
    return <StampsPanel documentId={documentId} />
  }

  return null
}

function StampsPanel({ documentId }: { documentId: string }) {
  const { t } = useTranslation()
  const { libraries } = useStampLibraries()
  const { provides: stampCapability } = useStampCapability()
  const activeStamp = useActiveStamp(documentId)

  const sidebarLibraries = libraries.filter((lib) =>
    lib.categories?.includes('sidebar'),
  )

  if (sidebarLibraries.length === 0) {
    return (
      <div className="p-8 flex flex-col items-center justify-center text-center gap-4 h-full">
        <div className="w-12 h-12 rounded-full bg-bg-surface flex items-center justify-center text-fg-muted">
          <Icon icon="rubberStamp" size={24} />
        </div>
        <div>
          <h4 className="text-fg-primary text-sm font-bold mb-1">{t('sidebar.noStamps')}</h4>
          <p className="text-fg-muted text-xs">{t('sidebar.noStampsHint')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto p-3 space-y-4">
      {sidebarLibraries.map((library) => (
        <StampsInLibrary
          key={library.id}
          library={library}
          documentId={documentId}
          stampCapability={stampCapability}
          activeStamp={activeStamp}
        />
      ))}
    </div>
  )
}

function StampsInLibrary({
  library,
  documentId,
  stampCapability,
  activeStamp,
}: {
  library: { id: string; name: string }
  documentId: string
  stampCapability: any
  activeStamp: any
}) {
  const stamps = useStampsByLibrary(library.id, 'sidebar')

  if (stamps.length === 0) return null

  return (
    <div>
      <h3 className="text-xs font-semibold text-fg-secondary uppercase tracking-wider mb-2 px-1">
        {library.name}
      </h3>
      <div className="grid grid-cols-2 gap-2">
        {stamps.map(({ library: lib, stamp }) => {
          const isActive =
            activeStamp?.libraryId === lib.id &&
            activeStamp?.stamp?.id === stamp.id

          return (
            <button
              key={`${lib.id}-${stamp.id}`}
              onClick={() => {
                stampCapability
                  ?.forDocument(documentId)
                  .activateStampPlacement(lib.id, stamp)
                  .wait(() => {}, ignore)
              }}
              className={`group flex flex-col items-center gap-2 rounded-xl border p-3 text-center transition-all ${
                isActive
                  ? 'border-accent bg-accent/10 shadow-sm ring-1 ring-accent'
                  : 'border-border-default hover:border-border-strong hover:bg-bg-surface-alt'
              }`}
            >
              <div className="flex aspect-square w-full items-center justify-center overflow-hidden rounded-lg bg-white">
                <StampImg
                  libraryId={lib.id}
                  pageIndex={stamp.pageIndex}
                  width={STAMP_THUMB_WIDTH}
                  style={{
                    maxWidth: '80%',
                    maxHeight: '80%',
                    objectFit: 'contain',
                  }}
                />
              </div>
              <div className="w-full">
                <div className="truncate text-[10px] font-medium text-fg-primary">
                  {stamp.label ?? stamp.subject}
                </div>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}
